/**
 * MDJ Curate — Blend Math Engine
 * Maison de Jarin / Kaarnika Foods
 *
 * assets/mdj-curate-engine.js
 *
 * Pure functions. No DOM. No section/globals dependency beyond the
 * MDJRitual-style namespace export at the bottom, which mirrors the
 * existing mdj-storage.js convention (IIFE, window.MDJCurate namespace)
 * so this file drops into the theme unchanged, but every function here
 * is also reachable via CommonJS export for isolated testing (Node).
 *
 * Ingredient input shape (fed from Liquid-rendered metaobject data,
 * NOT hardcoded here):
 *   {
 *     key: 'ginger',
 *     weight: 4.0,        // blend_weight_chai or blend_weight_green
 *     maxShare: 0.60,      // max_share_chai or max_share_green, 0-1
 *     canLead: true,
 *     isBase: false,       // green/black-herbal only
 *     isTrace: false       // chai only
 *   }
 *
 * Public API:
 *   computeChaiBlend(input)
 *   computeGreenBlend(input)
 */

(function () {
  'use strict';

  var EPSILON = 1e-9;
  var MAX_CLAMP_PASSES = 10;
  var FLOOR_WARNING_G = 3;

  // ── Shared helpers ──────────────────────────────────────────

  function proportionalSplit(items, totalMass) {
    var totalW = 0;
    for (var i = 0; i < items.length; i++) totalW += items[i].weight;
    if (totalW <= 0) {
      return items.map(function (it) {
        return assign({}, it, { mass: 0 });
      });
    }
    return items.map(function (it) {
      return assign({}, it, { mass: totalMass * (it.weight / totalW) });
    });
  }

  function assign(target) {
    for (var i = 1; i < arguments.length; i++) {
      var src = arguments[i];
      if (!src) continue;
      for (var k in src) {
        if (Object.prototype.hasOwnProperty.call(src, k)) target[k] = src[k];
      }
    }
    return target;
  }

  /**
   * Iteratively clamps any item whose mass exceeds capMass * item.maxShare,
   * redistributing the freed surplus among unclamped items proportional to
   * their weight. If absorberKey is supplied (green/black-herbal only),
   * the named item absorbs surplus first, up to its own ceiling, before
   * proportional redistribution to the rest.
   *
   * Runs to a fixed point or MAX_CLAMP_PASSES, whichever comes first.
   */
  function clampAndRedistribute(items, capMass, absorberKey) {
    var pool = items.map(function (it) {
      return assign({}, it, { clamped: !!it.clamped });
    });

    // Feasibility guard: if the selection's combined max_share ceilings
    // can't cover capMass, no split can both sum correctly AND respect
    // every cap. Fail loudly here rather than let the rounding-drift step
    // silently dump the unallocatable remainder onto one ingredient,
    // blowing past its own max_share. Callers (the capacity-vessel UI)
    // must prevent this combination from being reachable in the first
    // place — the vessel's capacity_cost heuristic does not by itself
    // guarantee max_share feasibility, so this needs an explicit check
    // on the UI side too.
    var totalCeiling = 0;
    for (var c = 0; c < pool.length; c++) totalCeiling += capMass * pool[c].maxShare;
    if (totalCeiling < capMass - EPSILON) {
      throw new Error(
        'clampAndRedistribute: infeasible selection — combined max_share ceilings (' +
        totalCeiling.toFixed(2) + 'g) cannot cover required mass (' + capMass.toFixed(2) +
        'g). Keys: ' + pool.map(function (it) { return it.key; }).join(', ')
      );
    }

    for (var pass = 0; pass < MAX_CLAMP_PASSES; pass++) {
      var surplus = 0;
      var overflowed = false;

      for (var i = 0; i < pool.length; i++) {
        var it = pool[i];
        if (it.clamped) continue;
        var ceiling = capMass * it.maxShare;
        if (it.mass > ceiling + EPSILON) {
          surplus += it.mass - ceiling;
          it.mass = ceiling;
          it.clamped = true;
          overflowed = true;
        }
      }

      if (!overflowed || surplus <= EPSILON) break;

      if (absorberKey) {
        var absorber = null;
        for (var a = 0; a < pool.length; a++) {
          if (pool[a].key === absorberKey && !pool[a].clamped) {
            absorber = pool[a];
            break;
          }
        }
        if (absorber) {
          var absorberCeiling = capMass * absorber.maxShare;
          var headroom = absorberCeiling - absorber.mass;
          var take = Math.min(headroom, surplus);
          if (take > EPSILON) {
            absorber.mass += take;
            surplus -= take;
          }
        }
      }

      if (surplus <= EPSILON) continue;

      var unclamped = pool.filter(function (it) {
        return !it.clamped;
      });
      var totalW = 0;
      for (var u = 0; u < unclamped.length; u++) totalW += unclamped[u].weight;
      if (totalW <= 0) break; // surplus stranded — nothing left to receive it

      for (var u2 = 0; u2 < unclamped.length; u2++) {
        unclamped[u2].mass += surplus * (unclamped[u2].weight / totalW);
      }
    }

    return pool;
  }

  /**
   * Rounds each item's mass (trace items to nearest 0.5g, others to
   * nearest 1g), then applies the entire rounding drift to the
   * unclamped item with the highest weight, so the displayed total
   * always sums to exactly `target`. `fixedLines` are non-proportional
   * rounded values (chai/herbal base) already summed into target
   * accounting — they participate in the total but never receive drift.
   */
  function roundWithDrift(items, target) {
    var rounded = items.map(function (it) {
      var step = it.isTrace ? 0.5 : 1;
      return assign({}, it, { grams: Math.round(it.mass / step) * step });
    });

    var sum = 0;
    for (var i = 0; i < rounded.length; i++) sum += rounded[i].grams;
    var drift = target - sum;

    if (Math.abs(drift) > EPSILON) {
      var candidates = rounded.filter(function (it) {
        return !it.clamped;
      });
      var pool = candidates.length ? candidates : rounded;
      var target_ = pool[0];
      for (var j = 1; j < pool.length; j++) {
        if (pool[j].weight > target_.weight) target_ = pool[j];
      }
      for (var k = 0; k < rounded.length; k++) {
        if (rounded[k].key === target_.key) {
          rounded[k].grams += drift;
          break;
        }
      }
    }

    return rounded;
  }

  function withPercents(items, total) {
    return items.map(function (it) {
      return assign({}, it, {
        pct: total > 0 ? (it.grams / total) * 100 : 0
      });
    });
  }

  function floorWarnings(items) {
    var warnings = [];
    items.forEach(function (it) {
      if (!it.isTrace && it.grams > 0 && it.grams < FLOOR_WARNING_G) {
        warnings.push({
          key: it.key,
          grams: it.grams,
          message: 'At this size, ' + it.key + ' will be barely present.'
        });
      }
    });
    return warnings;
  }

  // ── CHAI ────────────────────────────────────────────────────

  /**
   * @param {Object} input
   * @param {number} input.sizeGrams
   * @param {Array}  input.spices          selected spice ingredients (weight, maxShare, canLead, isTrace)
   * @param {string}  [input.leadKey]      key of a spice with canLead:true
   * @returns {Object} { base, spices, total, warnings }
   */
  function computeChaiBlend(input) {
    var S = input.sizeGrams;
    var spicesInput = input.spices || [];
    var leadKey = input.leadKey || null;

    var traceItems = spicesInput.filter(function (it) {
      return it.isTrace;
    });
    var nonTraceItems = spicesInput.filter(function (it) {
      return !it.isTrace;
    });

    var traceMass = 0;
    var tracedSpices = traceItems.map(function (it) {
      var mass = S * 0.002;
      traceMass += mass;
      return assign({}, it, { mass: mass, clamped: false });
    });

    var baseMass = S * 0.75;
    var spiceMass = S * 0.25 - traceMass;

    var spicePool;
    if (leadKey) {
      var leadIdx = -1;
      for (var i = 0; i < nonTraceItems.length; i++) {
        if (nonTraceItems[i].key === leadKey) {
          leadIdx = i;
          break;
        }
      }
      if (leadIdx === -1) {
        throw new Error('computeChaiBlend: leadKey "' + leadKey + '" is not among selected non-trace spices.');
      }
      if (!nonTraceItems[leadIdx].canLead) {
        throw new Error('computeChaiBlend: "' + leadKey + '" is not eligible to lead (canLead: false).');
      }
      var lead = nonTraceItems[leadIdx];
      var others = nonTraceItems.slice(0, leadIdx).concat(nonTraceItems.slice(leadIdx + 1));

      var leadCeiling = spiceMass * lead.maxShare;
      var leadMass = Math.min(spiceMass * 0.60, leadCeiling);
      var remainder = spiceMass - leadMass;

      var splitOthers = proportionalSplit(others, remainder);
      spicePool = [assign({}, lead, { mass: leadMass })].concat(splitOthers);
    } else {
      spicePool = proportionalSplit(nonTraceItems, spiceMass);
    }

    var clampedSpices = clampAndRedistribute(spicePool, spiceMass, null);

    // allForRounding holds BOTH the trace items (fixed dose, traceMass total)
    // and the proportionally-split non-trace spices (spiceMass total) — the
    // rounding target must be their combined mass (the full 25% allocation,
    // i.e. spiceMass + traceMass), not spiceMass alone, or trace selections
    // silently shrink the displayed total below S.
    var allForRounding = tracedSpices.concat(clampedSpices);
    var spiceAllocation = spiceMass + traceMass;
    var roundedSpices = roundWithDrift(allForRounding, spiceAllocation);

    var roundedBase = Math.round(baseMass);

    // Structural (base) rounding drift folds back into the spice pool's
    // drift target so the whole sheet — not just the spice column — sums
    // to exactly S.
    var structuralDrift = baseMass - roundedBase;
    if (Math.abs(structuralDrift) > EPSILON) {
      roundedSpices = roundWithDrift(
        allForRounding.map(function (it, idx) {
          return assign({}, it, { mass: it.mass }); // re-derive from unrounded mass
        }),
        spiceAllocation + structuralDrift
      );
    }

    var total = roundedBase;
    for (var s = 0; s < roundedSpices.length; s++) total += roundedSpices[s].grams;

    var spicesWithPct = withPercents(roundedSpices, S);

    return {
      base: { key: 'base', grams: roundedBase, pct: (roundedBase / S) * 100 },
      spices: spicesWithPct,
      total: total,
      target: S,
      warnings: floorWarnings(roundedSpices)
    };
  }

  // ── GREEN / BLACK HERBAL ────────────────────────────────────
  //
  // Locked-base model (revised): the tea (Whole Leaf Green Tea / Whole
  // Leaf Black Orthodox) is fixed at baseShare of the total — NOT part
  // of the selectable/proportional pool at all — mirroring chai's
  // structure (fixed base + proportional split of the remainder) rather
  // than the original "tea is one equal ingredient among the herbs"
  // design. Default baseShare is 0.5 (50% tea / 50% herbs), per explicit
  // house correction. This intentionally changes the numeric outcome of
  // the original Holy Basil example (tea/vana/rama/peppermint would now
  // split 50 / 16.7 / 16.7 / 16.7 instead of 25 each) — that's the
  // direct, unavoidable consequence of moving to a fixed 50% base;
  // flagging it rather than silently reconciling the two.
  //
  // Because the base is now fixed regardless of selection count, the
  // earlier "house-norm slot absorption" mechanic (tea flexing upward
  // when fewer herbs are chosen) no longer applies and has been removed
  // — there is nothing left for it to do once the base isn't competing
  // for share with the herbs.

  var DEFAULT_HERBAL_BASE_SHARE = 0.5;
  var HERBAL_LEAD_SHARE = 0.60; // mirrors chai's spice-pool lead share, applied to herbMass instead of S

  /**
   * @param {Object} input
   * @param {number} input.sizeGrams
   * @param {Array}  input.ingredients   selected HERBS only — do not include the base tea itself (weight, maxShare, canLead)
   * @param {string} input.baseKey       key of the fixed base tea (green_tea or black_orthodox) — for labeling only, not part of the pool
   * @param {number} [input.baseShare]   fraction of total the base occupies, default 0.5
   * @param {string} [input.leadKey]     must be a herb key; the base tea is never lead-eligible (its share is fixed)
   * @returns {Object} { base, herbs, total, warnings }
   */
  function computeGreenBlend(input) {
    var S = input.sizeGrams;
    var baseKey = input.baseKey;
    var baseShare = input.baseShare != null ? input.baseShare : DEFAULT_HERBAL_BASE_SHARE;
    var leadKey = input.leadKey || null;
    var herbsInput = (input.ingredients || []).filter(function (it) { return it.key !== baseKey; });

    var baseMass = S * baseShare;
    var herbMass = S - baseMass;

    var pool;
    if (leadKey) {
      var leadIdx = -1;
      for (var i = 0; i < herbsInput.length; i++) {
        if (herbsInput[i].key === leadKey) {
          leadIdx = i;
          break;
        }
      }
      if (leadIdx === -1) {
        throw new Error('computeGreenBlend: leadKey "' + leadKey + '" is not among selected herbs (the base tea is never lead-eligible).');
      }
      if (!herbsInput[leadIdx].canLead) {
        throw new Error('computeGreenBlend: "' + leadKey + '" is not eligible to lead (canLead: false).');
      }
      var lead = herbsInput[leadIdx];
      var others = herbsInput.slice(0, leadIdx).concat(herbsInput.slice(leadIdx + 1));

      var leadCeiling = herbMass * lead.maxShare;
      var leadMass = Math.min(herbMass * HERBAL_LEAD_SHARE, leadCeiling);
      var remainder = herbMass - leadMass;

      var splitOthers = proportionalSplit(others, remainder);
      pool = [assign({}, lead, { mass: leadMass })].concat(splitOthers);
    } else {
      pool = proportionalSplit(herbsInput, herbMass);
    }

    var clamped = clampAndRedistribute(pool, herbMass, null); // no absorber — the base no longer participates at all
    var roundedBase = Math.round(baseMass);
    var roundedHerbs = roundWithDrift(clamped, herbMass);

    var structuralDrift = baseMass - roundedBase;
    if (Math.abs(structuralDrift) > EPSILON) {
      roundedHerbs = roundWithDrift(clamped, herbMass + structuralDrift);
    }

    var total = roundedBase;
    for (var j = 0; j < roundedHerbs.length; j++) total += roundedHerbs[j].grams;

    return {
      base: { key: baseKey, grams: roundedBase, pct: (roundedBase / S) * 100 },
      herbs: withPercents(roundedHerbs, S),
      total: total,
      target: S,
      warnings: floorWarnings(roundedHerbs)
    };
  }

  var api = {
    computeChaiBlend: computeChaiBlend,
    computeGreenBlend: computeGreenBlend,
    // exposed for test/introspection use only
    _internal: {
      proportionalSplit: proportionalSplit,
      clampAndRedistribute: clampAndRedistribute,
      roundWithDrift: roundWithDrift
    }
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  if (typeof window !== 'undefined') {
    window.MDJCurate = window.MDJCurate || {};
    window.MDJCurate.Engine = api;
  }
})();
